import React from "react";
import { ArrowLeft, Calendar, Clock, User } from "lucide-react";

interface BlogLayoutProps {
  children: React.ReactNode;
  onBack: () => void;
  title?: string;
  showBackButton?: boolean;
}

export function BlogLayout({ children, onBack, title, showBackButton = true }: BlogLayoutProps) {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {showBackButton && (
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Posts
          </button>
        )}
        
        {title && (
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{title}</h1>
          </div>
        )}
        
        {children}
      </div>
    </div>
  );
}

interface BlogMetaProps {
  author: string;
  publishedAt: string;
  readTime: number;
  category?: string;
  tags?: string[];
}

export function BlogMeta({ author, publishedAt, readTime, category, tags }: BlogMetaProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
      <div className="flex items-center gap-1">
        <User className="w-4 h-4" />
        <span>{author}</span>
      </div>
      
      <div className="flex items-center gap-1">
        <Calendar className="w-4 h-4" />
        <span>{formatDate(publishedAt)}</span>
      </div>
      
      <div className="flex items-center gap-1">
        <Clock className="w-4 h-4" />
        <span>{readTime} min read</span>
      </div>
      
      {category && (
        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
          {category}
        </span>
      )}
      
      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag, index) => (
            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
